import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n, EVENT, SECTION_IMAGES } from "@/lib/i18n";
import { motion } from "framer-motion";
import { usePageSEO, SEO } from "@/lib/seo";
import SocialRunPage from "./SocialRunPage";

/* ────────────────────────────  Events list  ──────────────────────────── */
export function EventsList() {
  const { t, pick } = useI18n();
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/content", { params: { kind: "event" } }).then((r) => setEvents(r.data)).catch(() => {});
  }, []);
  return (
    <div className="container-x max-editorial py-20">
      <div className="label-mono text-terracotta">{t("nav_events")}</div>
      <h1 className="font-editorial text-5xl md:text-7xl mt-3 tracking-tighter">{t("Vores rejser.","Our journeys.","Nuestros viajes.")}</h1>
      <p className="mt-4 text-ink/70 max-w-xl">{t("Kommende og forgangne løb, samtaler og samlinger.","Upcoming and past runs, conversations and gatherings.","Carreras, conversaciones y encuentros, pasados y por venir.")}</p>
      <div className="mt-16 grid md:grid-cols-2 gap-10">
        <Link
          to="/events/social-run-for-spain"
          data-testid="event-card-social-run-for-spain"
          className="group block"
        >
          <div className="aspect-[16/10] overflow-hidden bg-ink/5">
            <img
              src={SECTION_IMAGES.socialRun.image}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          </div>
          <div className="mt-5 flex justify-between items-baseline">
            <h2 className="font-editorial text-3xl">Social Run for Spain</h2>
            <span className="label-mono text-ink/60">06 · 09 · 2026</span>
          </div>
          <p className="mt-2 text-ink/70">
            Fælledparken, København · 5 km · Live musik & spansk gastronomi.
          </p>
        </Link>
        {events
          .filter((e) => e?.data?.slug && e.data.slug !== "social-run-for-spain")
          .map((e) => (
            <Link
              key={e.id}
              to={`/events/${e.data.slug}`}
              data-testid={`event-card-${e.data.slug}`}
              className="group block"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={e.data.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="mt-5 flex justify-between items-baseline">
                <h2 className="font-editorial text-3xl">{pick(e.data.title)}</h2>
                <span className="label-mono text-ink/60">{e.data.date_iso}</span>
              </div>
              <p className="mt-2 text-ink/70">{pick(e.data.tagline)}</p>
            </Link>
          ))}
      </div>
    </div>
  );
}

/* ────────────────────────────  Event guide  ──────────────────────────── */
const HERO_IMAGE = "/images/community-event.webp";

function useT() {
  const { lang } = useI18n();
  return (da, en, es) => ({ da, en, es })[lang] || en;
}

/* Countdown — self-contained, mirrors the homepage widget so we can preserve it. */
function EventCountdown({ dateIso }) {
  const { t } = useI18n();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(dateIso).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff / 3600000) % 24);
  const m = Math.floor((diff / 60000) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return (
    <div data-testid="event-countdown" className="flex gap-6 md:gap-9 text-center">
      {[[d, t("countdown_days")], [h, t("countdown_hours")], [m, t("countdown_min")], [s, t("countdown_sec")]].map(
        ([v, l]) => (
          <div key={l}>
            <div className="font-editorial text-3xl md:text-5xl leading-none tabular-nums text-white">
              {String(v).padStart(2, "0")}
            </div>
            <div className="label-mono text-white/70 mt-2">{l}</div>
          </div>
        )
      )}
    </div>
  );
}

/* ────────────────────  Public Social Run event guide  ──────────────── */
export function EventDetail() {
  const { slug } = useParams();
  const t = useT();
  const { lang } = useI18n();
  usePageSEO(SEO.socialRun.title, SEO.socialRun.description);

  // Recap check — when the event admin has set recap_state='live' we render
  // the new editorial archive page instead of the legacy landing. Falls
  // back to the old layout on any error or when disabled.
  // Preview mode: /events/social-run-for-spain?preview=1 tries the admin
  // preview endpoint. If the caller isn't an authenticated admin the
  // request 401s and we silently fall back to whatever state the public
  // endpoint returns — no draft/hidden content ever leaks.
  const [recap, setRecap] = useState(null);
  useEffect(() => {
    const previewMode = new URLSearchParams(window.location.search).get("preview") === "1";
    const load = async () => {
      if (previewMode) {
        try {
          const r = await api.get("/admin/social-run/preview", { withCredentials: true });
          setRecap({ ...r.data, enabled: true });
          return;
        } catch {
          // Not an admin — fall through to the public endpoint.
        }
      }
      try {
        const r = await api.get("/social-run");
        setRecap(r.data);
      } catch {
        setRecap({ enabled: false, sections: [] });
      }
    };
    load();
  }, []);

  const [programme, setProgramme] = useState([]);
  useEffect(() => {
    api.get("/programme").then(r => setProgramme(r.data || [])).catch(() => setProgramme([]));
  }, []);

  // Render new recap page when admin has flipped the flag and at least
  // one section is published. Hooks above run unconditionally on every
  // render so the early return is safe.
  if (recap?.enabled && (recap?.sections?.length || 0) > 0) {
    return <SocialRunPage sections={recap.sections} seo={recap.seo} />;
  }

  const pickTitle = (row) => row[`title_${lang}`] || row.title_en || row.title_da || "";
  const pickDesc = (row) => row[`description_${lang}`] || row.description_en || row.description_da || "";

  // The page is scoped to the Social Run guide.
  const targetSlug = slug === EVENT.slug ? "social-run-for-spain" : (slug || "social-run-for-spain");
  const registerHref = `/events/${targetSlug}/register`;
  const countdownIso = `${EVENT.dateIso}T${EVENT.time}:00+02:00`;

  const glance = [
    {
      k: t("Dato", "Date", "Fecha"),
      v: t("Søndag 6. september 2026", "Sunday 6 September 2026", "Domingo 6 de septiembre de 2026"),
      testid: "glance-date",
    },
    {
      k: t("Lokation", "Location", "Ubicación"),
      v: t("Fælledparken, København", "Fælledparken, Copenhagen", "Fælledparken, Copenhague"),
      testid: "glance-location",
    },
    { k: t("Distance", "Distance", "Distancia"), v: "5 km", testid: "glance-distance" },
    {
      k: t("For hvem", "Suitable for", "Para quién"),
      v: t(
        "Alle · familier · gående · løbere · virksomheder",
        "Everyone · families · walkers · runners · companies",
        "Todos · familias · caminantes · corredores · empresas"
      ),
      testid: "glance-audience",
    },
    {
      k: t("Deltagergebyr", "Participation fee", "Cuota de participación"),
      v: t("Fra 50 DKK", "From 50 DKK", "Desde 50 DKK"),
      testid: "glance-fee",
    },
  ];

  // NOTE: The Social Run programme is CMS-managed via /api/programme (see useEffect above).
  // No static array here — the timeline reads directly from the `programme` state.

  const foodItems = [
    t("Paella", "Paella", "Paella"),
    t("Tapas", "Tapas", "Tapas"),
    t("Spanske desserter", "Spanish desserts", "Postres españoles"),
    t("Sodavand", "Soft drinks", "Refrescos"),
    t("Kaffe", "Coffee", "Café"),
    t("Øl", "Beer", "Cerveza"),
    t("Vin", "Wine", "Vino"),
  ];

  const whyCards = [
    {
      k: "01",
      title: t("Løb for en sag.", "Run for a cause.", "Corre por una causa."),
      body: t(
        "Hvert skridt bliver til reel støtte til de familier, DANAmark stands beside.",
        "Every step becomes real support for the families DANAmark stands beside.",
        "Cada paso se convierte en apoyo real para las familias con las que DANAmark acompaña."
      ),
    },
    {
      k: "02",
      title: t("Mød nye mennesker.", "Meet new people.", "Conoce a nuevas personas."),
      body: t(
        "En eftermiddag hvor Danmark og Spanien mødes gennem løb, mad, musik og samtaler.",
        "An afternoon where Denmark and Spain meet through running, food, music and conversation.",
        "Una tarde donde Dinamarca y España se encuentran a través del running, la comida, la música y la conversación."
      ),
    },
    {
      k: "03",
      title: t("Støt Spanien.", "Support Spain.", "Apoya a España."),
      body: t(
        "Hver tilmelding rejser midler til fællesskaber, der er ramt af katastrofer i Spanien.",
        "Every registration raises funds for communities affected by disasters in Spain.",
        "Cada inscripción recauda fondos para las comunidades afectadas por catástrofes en España."
      ),
    },
    {
      k: "04",
      title: t("Fejr fællesskabet.", "Celebrate community.", "Celebra la comunidad."),
      body: t(
        "Ingen tid. Ingen konkurrence. Kun mennesker, der løber sammen for noget større.",
        "No timing. No competition. Just people running together for something bigger.",
        "Sin cronómetro. Sin competición. Solo personas corriendo juntas por algo más grande."
      ),
    },
  ];

  const faqs = [
    {
      q: t("Kan jeg gå i stedet for at løbe?", "Can I walk instead of running?", "¿Puedo caminar en lugar de correr?"),
      a: t("Ja.", "Yes.", "Sí."),
    },
    {
      q: t("Kan børn deltage?", "Can children participate?", "¿Pueden participar los niños?"),
      a: t("Ja.", "Yes.", "Sí."),
    },
    {
      q: t(
        "Kan jeg tilmelde mig på dagen?",
        "Can I register on the day?",
        "¿Puedo inscribirme el mismo día?"
      ),
      a: t(
        "Hvis der stadig er ledige pladser.",
        "If places are still available.",
        "Si aún quedan plazas disponibles."
      ),
    },
    {
      q: t("Bliver tiden målt?", "Is the event timed?", "¿Se cronometra el evento?"),
      a: t(
        "Nej. Det er en social run — ikke en konkurrence.",
        "No. This is a social run, not a competition.",
        "No. Es una social run, no una competición."
      ),
    },
    {
      q: t("Hvor går pengene hen?", "Where does the money go?", "¿A dónde va el dinero?"),
      a: t(
        "Til DANAmark's projects, der støtter samfund ramt af katastrofer i Spanien.",
        "To DANAmark projects supporting communities affected by disasters in Spain.",
        "A los proyectos de DANAmark que apoyan a comunidades afectadas por catástrofes en España."
      ),
    },
    {
      q: t(
        "Hvad sker der, hvis det regner?",
        "What happens if it rains?",
        "¿Qué pasa si llueve?"
      ),
      a: t(
        "Eventet gennemføres, medmindre vejret gør det uforsvarligt.",
        "The event will take place unless weather conditions make it unsafe.",
        "El evento se celebrará salvo que las condiciones meteorológicas lo hagan inseguro."
      ),
    },
  ];

  return (
    <div>
      {/* ─────────── HERO ─────────── */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="socialrun-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/20" />
        <div className="relative container-x max-editorial pt-32 pb-16 md:pb-24 text-white">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="label-mono text-terracotta mb-6" data-testid="socialrun-hero-kicker">
              {t("VORES EVENT · 6. SEPTEMBER 2026", "OUR EVENT · 6 SEPTEMBER 2026", "NUESTRO EVENTO · 6 DE SEPTIEMBRE DE 2026")}
            </div>
            <h1
              data-testid="socialrun-hero-title"
              className="font-editorial text-5xl md:text-7xl lg:text-[104px] tracking-tighter leading-[0.94] max-w-5xl"
            >
              Social Run for Spain
            </h1>
            <p
              data-testid="socialrun-hero-subtitle"
              className="mt-6 md:mt-8 font-editorial text-2xl md:text-4xl lg:text-5xl text-white/95 max-w-4xl leading-[1.1] tracking-tight"
            >
              {t(
                "Løb sammen. Fejr sammen. Gør en forskel sammen.",
                "Run together. Celebrate together. Make a difference together.",
                "Corramos juntos. Celebremos juntos. Marquemos la diferencia juntos."
              )}
            </p>
            <p
              data-testid="socialrun-hero-intro"
              className="mt-8 md:mt-10 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed"
            >
              {t(
                "Søndag 6. september 2026 samles mennesker fra Danmark og mange andre lande i Fælledparken, København, til et 5 km løb, efterfulgt af en eftermiddag med live musik, spansk gastronomi og fællesskab.",
                "On Sunday 6 September 2026, people from Denmark and many other countries will come together in Fælledparken, Copenhagen, for a 5 km community run followed by an afternoon of live music, Spanish gastronomy and community.",
                "El domingo 6 de septiembre de 2026, personas de Dinamarca y de muchos otros países se reunirán en Fælledparken, Copenhague, para una carrera comunitaria de 5 km, seguida de una tarde con música en directo, gastronomía española y comunidad."
              )}
            </p>

            <div className="mt-12 md:mt-14 flex flex-col sm:flex-row sm:items-end gap-10">
              <EventCountdown dateIso={countdownIso} />
              <Link
                to={registerHref}
                data-testid="socialrun-hero-cta"
                className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-10 py-5 text-sm md:text-base tracking-wide self-start sm:self-end transition-colors"
              >
                {t("Tilmeld dig løbet", "Register now", "Inscríbete ahora")} →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─────────── EVENT AT A GLANCE ─────────── */}
      <section className="border-t border-line">
        <div className="container-x max-editorial py-24 md:py-36">
          <div className="max-w-2xl">
            <div className="label-mono text-terracotta">
              {t("EVENTET KORT FORTALT", "EVENT AT A GLANCE", "EL EVENTO EN RESUMEN")}
            </div>
            <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Alt du behøver at vide.", "Everything you need to know.", "Todo lo que necesitas saber.")}
            </h2>
          </div>
          <dl
            data-testid="event-glance"
            className="mt-14 md:mt-20 grid md:grid-cols-2 lg:grid-cols-5 border-t border-line"
          >
            {glance.map((row) => (
              <div
                key={row.testid}
                data-testid={row.testid}
                className="py-8 md:py-10 md:px-8 border-b md:border-b-0 md:border-r last:md:border-r-0 border-line"
              >
                <dt className="label-mono text-ink/50">{row.k}</dt>
                <dd className="mt-3 font-editorial text-xl md:text-[22px] leading-snug tracking-tight text-ink">
                  {row.v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ─────────── THE ROUTE ─────────── */}
      <section className="bg-ink text-white">
        <div className="container-x max-editorial py-24 md:py-36 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <div className="label-mono text-terracotta">{t("RUTEN", "THE ROUTE", "EL RECORRIDO")}</div>
            <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("5 km gennem Fælledparken.", "5 km through Fælledparken.", "5 km por Fælledparken.")}
            </h2>
            <p className="mt-8 text-lg md:text-xl text-white/85 leading-relaxed">
              {t(
                "Deltagerne gennemfører en cirka 5 km social rute gennem Fælledparken.",
                "Participants will complete an approximately 5 km social route through Fælledparken.",
                "Los participantes completarán un recorrido social de aproximadamente 5 km por Fælledparken."
              )}
            </p>
            <p className="mt-5 text-lg md:text-xl text-white/85 leading-relaxed">
              {t(
                "Dette er ikke et konkurrenceløb. Alle er velkomne — uanset alder eller form.",
                "This is not a competitive race. Everyone is welcome, regardless of age or fitness level.",
                "No es una carrera competitiva. Todos son bienvenidos, sin importar la edad o el nivel físico."
              )}
            </p>
          </div>
          <div className="md:col-span-7">
            <div
              data-testid="route-map-placeholder"
              className="aspect-[4/3] w-full border border-white/15 bg-white/[0.04] flex items-center justify-center px-6 text-center"
            >
              <div>
                <div className="label-mono text-white/50">{t("KOMMER SNART", "COMING SOON", "PRÓXIMAMENTE")}</div>
                <div className="mt-4 font-editorial text-2xl md:text-3xl text-white/85">
                  {t("Rutekort kommer snart", "Route map coming soon", "Mapa del recorrido próximamente")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── PROGRAMME ─────────── */}
      <section id="programme" className="border-t border-line">
        <div className="container-x max-editorial py-24 md:py-36">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="label-mono text-terracotta">{t("PROGRAM", "PROGRAMME", "PROGRAMA")}</div>
              <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
                {t("Dagen vi løber sammen.", "The day we run together.", "El día que corremos juntos.")}
              </h2>
            </div>
            <ol data-testid="programme-timeline" className="md:col-span-7 md:col-start-6 relative">
              <span aria-hidden className="absolute left-[92px] md:left-[110px] top-2 bottom-2 w-px bg-line" />
              {programme.map((row, i) => (
                <li
                  key={row.id || row.time}
                  data-testid={`programme-row-${row.time.replace(":", "")}`}
                  className={`relative grid grid-cols-[72px_1fr] md:grid-cols-[92px_1fr] gap-8 md:gap-12 py-6 md:py-8 ${
                    i < programme.length - 1 ? "border-b border-line" : ""
                  }`}
                >
                  <div className="font-editorial text-2xl md:text-3xl tabular-nums text-ink">{row.time}</div>
                  <div>
                    <div className="font-editorial text-xl md:text-2xl leading-snug">{pickTitle(row)}</div>
                    <div className="mt-2 text-ink/60 text-base leading-relaxed">{pickDesc(row)}</div>
                  </div>
                </li>
              ))}
              {/* KB Hallen references removed — the event was cancelled and
                  retired; no mention anywhere on public /events. */}
            </ol>
          </div>
        </div>
      </section>

      {/* ─────────── SPANISH FOOD ─────────── */}
      <section className="bg-off-white border-t border-line" style={{ backgroundColor: "#F6F1EA" }}>
        <div className="container-x max-editorial py-24 md:py-36 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <div className="label-mono text-terracotta">{t("SPANSK MAD", "SPANISH FOOD", "GASTRONOMÍA ESPAÑOLA")}</div>
            <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Spanske smage i København.", "Spanish flavours in Copenhagen.", "Sabores españoles en Copenhague.")}
            </h2>
            <p className="mt-8 text-lg md:text-xl text-ink/80 leading-relaxed">
              {t(
                "Gæsterne kan nyde autentisk spansk mad gennem hele eftermiddagen.",
                "Visitors will enjoy authentic Spanish food throughout the afternoon.",
                "Los asistentes disfrutarán de auténtica comida española durante toda la tarde."
              )}
            </p>
            <p className="mt-4 text-base text-ink/55 leading-relaxed">
              {t(
                "Vi reserverer plads til kommende madpartnere.",
                "We are reserving space for future food partners.",
                "Reservamos espacio para futuros socios gastronómicos."
              )}
            </p>
          </div>
          <ul
            data-testid="food-list"
            className="md:col-span-6 md:col-start-7 grid grid-cols-2 gap-x-8 gap-y-5 self-center"
          >
            {foodItems.map((item, i) => (
              <li
                key={item}
                data-testid={`food-item-${i}`}
                className="font-editorial text-2xl md:text-3xl text-ink leading-snug border-b border-ink/10 pb-4"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─────────── LIVE MUSIC ─────────── */}
      <section className="border-t border-line">
        <div className="container-x max-editorial py-24 md:py-36 max-w-4xl">
          <div className="label-mono text-terracotta">{t("LIVE MUSIK", "LIVE MUSIC", "MÚSICA EN DIRECTO")}</div>
          <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
            {t(
              "Musik der samler mennesker.",
              "Music that brings people together.",
              "Música que une a las personas."
            )}
          </h2>
          <p className="mt-8 text-lg md:text-xl text-ink/80 leading-relaxed">
            {t(
              "Fejringen fortsætter med live optrædener efter løbet.",
              "The celebration continues with live performances after the run.",
              "La celebración continúa con actuaciones en directo tras la carrera."
            )}
          </p>
          <div
            data-testid="artists-placeholder"
            className="mt-12 border border-line px-8 md:px-10 py-10 md:py-12"
          >
            <div className="label-mono text-ink/50">{t("KOMMER SNART", "COMING SOON", "PRÓXIMAMENTE")}</div>
            <div className="mt-3 font-editorial text-2xl md:text-3xl leading-snug">
              {t(
                "Kunstnere annonceres snart.",
                "Artists will be announced soon.",
                "Los artistas se anunciarán próximamente."
              )}
            </div>
            <p className="mt-4 text-ink/55">
              {t(
                "Vi reserverer plads til fremtidige kunstnere.",
                "We are reserving space for future artist announcements.",
                "Reservamos espacio para futuros anuncios de artistas."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* ─────────── WHY JOIN ─────────── */}
      <section className="bg-ink text-white">
        <div className="container-x max-editorial py-24 md:py-36">
          <div className="max-w-2xl">
            <div className="label-mono text-terracotta">{t("HVORFOR DELTAGE?", "WHY JOIN?", "¿POR QUÉ PARTICIPAR?")}</div>
            <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Fire grunde til at være der.", "Four reasons to be there.", "Cuatro razones para estar allí.")}
            </h2>
          </div>
          <div data-testid="why-cards" className="mt-16 md:mt-20 grid md:grid-cols-2 gap-px bg-white/10">
            {whyCards.map((card) => (
              <article
                key={card.k}
                data-testid={`why-card-${card.k}`}
                className="bg-ink p-10 md:p-14"
              >
                <div className="label-mono text-terracotta">{card.k}</div>
                <h3 className="mt-4 font-editorial text-3xl md:text-4xl leading-[1.1] tracking-tight">{card.title}</h3>
                <p className="mt-5 text-white/75 text-lg leading-relaxed">{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FAQ ─────────── */}
      <section className="border-t border-line">
        <div className="container-x max-editorial py-24 md:py-36 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="label-mono text-terracotta">{t("OFTE STILLEDE SPØRGSMÅL", "FREQUENTLY ASKED QUESTIONS", "PREGUNTAS FRECUENTES")}</div>
            <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Godt at vide.", "Good to know.", "Bueno saberlo.")}
            </h2>
          </div>
          <div data-testid="faq-list" className="md:col-span-7 md:col-start-6">
            {faqs.map((f, i) => (
              <details
                key={f.q}
                data-testid={`faq-${i}`}
                className="border-b border-line py-6 group"
              >
                <summary className="cursor-pointer flex items-start justify-between gap-6 font-editorial text-xl md:text-2xl leading-snug list-none">
                  <span>{f.q}</span>
                  <span
                    aria-hidden
                    className="mt-1 label-mono text-terracotta transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-5 text-ink/75 text-lg leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FINAL REGISTER CTA ─────────── */}
      <section className="bg-terracotta text-white">
        <div className="container-x max-editorial py-28 md:py-48">
          <div className="max-w-4xl">
            <div className="label-mono text-white/80">{t("TILMELDING", "REGISTRATION", "INSCRIPCIÓN")}</div>
            <h2
              data-testid="register-final-headline"
              className="mt-4 font-editorial text-5xl md:text-6xl lg:text-8xl leading-[1.0] tracking-tighter"
            >
              {t("Klar til at løbe med os?", "Ready to run with us?", "¿Listo para correr con nosotros?")}
            </h2>
            <p className="mt-10 text-white/95 text-xl md:text-2xl leading-[1.55] font-light max-w-3xl">
              {t(
                "Hver tilmelding hjælper med at forvandle idéer til reel støtte.",
                "Every registration helps transform ideas into real support.",
                "Cada inscripción ayuda a transformar las ideas en apoyo real."
              )}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6">
              <Link
                to={registerHref}
                data-testid="register-final-cta"
                className="inline-flex items-center gap-3 bg-white text-terracotta hover:bg-ink hover:text-white px-10 md:px-12 py-5 md:py-6 text-base md:text-lg tracking-wide font-medium transition-colors"
              >
                {t("Tilmeld dig nu", "Register now", "Inscríbete ahora")} →
              </Link>
              <div
                data-testid="mobilepay-placeholder"
                className="text-white/80 text-sm md:text-base leading-relaxed"
              >
                {t(
                  "Betaling: kort · Apple Pay · Google Pay · MobilePay Box (snart)",
                  "Payment: card · Apple Pay · Google Pay · MobilePay Box (coming soon)",
                  "Pago: tarjeta · Apple Pay · Google Pay · MobilePay Box (próximamente)"
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile sticky register bar — kept for parity with previous UX */}
      <div className="fixed md:hidden bottom-0 left-0 right-0 bg-ink z-40">
        <Link
          to={registerHref}
          data-testid="socialrun-mobile-register"
          className="flex items-center justify-center py-4 text-white text-sm tracking-wide"
        >
          {t("Tilmeld dig", "Register now", "Inscríbete ahora")} →
        </Link>
      </div>
    </div>
  );
}
